import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, guardarSesion, leerSesion, puede, incluye, modoConsultorio } from "../api.ts";
import { ACENTOS, DESCRIPCION_ROL, LOGO_MAX_BYTES, paletaDeAcento, rucValido, bannerPorDefecto, type RolUsuario } from "../../../dominio/src/index.ts";
import { aplicarAcento, publicarClinica, useClinica, enlaceIngreso, type Clinica, type Sede } from "../clinica.ts";
import { AvisoError, CampoClave, Seccion, useAccion } from "./comun.tsx";
import { PanelConfigMensajes } from "./Mensajes.tsx";
import { ImportarPacientes } from "./Importar.tsx";
import { PanelFacturacion } from "./Facturas.tsx";
import { PanelPlan } from "./Suscripcion.tsx";
import { PanelTratamientos } from "./Tratamientos.tsx";
import { BannerClinica } from "../componentes/BannerClinica.tsx";
import { InvitarProfesional } from "../componentes/Invitaciones.tsx";
import { prepararLogo } from "../imagen.ts";
import { ConfigUniversidad } from "./Universidad.tsx";
import { ConfigGemini } from "./ConfigGemini.tsx";
import { DuplicadosPacientes } from "./DuplicadosPacientes.tsx";
import { LicenciaConsultorio } from "./LicenciaConsultorio.tsx";
import { PacientesPrueba } from "./PacientesPrueba.tsx";
import { FeriadosClinica } from "./Feriados.tsx";
import { DosPasos } from "../componentes/DosPasos.tsx";
import { VozDeDonna } from "../componentes/VozDeDonna.tsx";

const PESTANAS: { clave: string; nombre: string; permiso: string | null; funcion?: string }[] = [
  { clave: "empresa", nombre: "Empresa y marca", permiso: "config:admin" },
  { clave: "sedes", nombre: "Sedes", permiso: "config:admin" },
  { clave: "usuarios", nombre: "Usuarios y roles", permiso: "usuarios:admin" },
  { clave: "horario", nombre: "Horario de atención", permiso: "config:admin" },
  { clave: "tratamientos", nombre: "Tratamientos y precios", permiso: "config:admin" },
  { clave: "documentos", nombre: "Documentos y cobros", permiso: "config:admin" },
  { clave: "facturacion", nombre: "Facturación electrónica", permiso: "config:admin", funcion: "facturacion" },
  { clave: "mensajes", nombre: "Mensajes y WhatsApp", permiso: "mensajes:admin" },
  { clave: "gemini", nombre: "IA de imágenes", permiso: "config:admin" },
  { clave: "universidad", nombre: "Clínica universitaria", permiso: "academico:admin" },
  { clave: "importar", nombre: "Importar pacientes", permiso: "pacientes:importar", funcion: "importar" },
  { clave: "duplicados", nombre: "Pacientes duplicados", permiso: "config:admin" },
  { clave: "pruebas", nombre: "Pacientes de prueba", permiso: "config:admin" },
  { clave: "respaldos", nombre: "Respaldos", permiso: "respaldos:admin" },
  { clave: "copias", nombre: "Copias de mi clínica", permiso: "respaldos:admin" },
  { clave: "plan", nombre: "Plan y suscripción", permiso: "config:admin", funcion: "__licencia" },
  { clave: "cuenta", nombre: "Mi cuenta", permiso: null },
];

export function Configuracion({ ir, pestana }: { ir: (r: string) => void; pestana: string }) {
  const conPlanes = !!leerSesion()?.usuario.licencia?.aplica;
  // «Respaldos» (toda la base del servidor) solo es de la clínica dueña de la instalación: a las demás no se les muestra
  const [duena, setDuena] = useState(false);
  useEffect(() => { if (puede("respaldos:admin")) api<{ duena: boolean }>("GET", "/api/respaldos/alerta").then((r) => setDuena(r.duena)).catch(() => setDuena(false)); }, []);
  const consultorio = modoConsultorio();
  const visibles = PESTANAS.filter((p) => !(consultorio && p.clave === "gemini")).map((p) => (consultorio && p.clave === "plan" ? { ...p, nombre: "Licencia" } : p)).filter((p) => (p.clave !== "respaldos" || duena) && (!p.permiso || puede(p.permiso)) && (p.funcion === "__licencia" ? conPlanes : !p.funcion || incluye(p.funcion)));
  const actual = visibles.find((p) => p.clave === pestana) ?? visibles[0];
  return (
    <div className="cfg-layout">
      <nav className="cfg-menu" aria-label="Secciones de configuración">
        {visibles.map((p) => (
          <button key={p.clave} aria-pressed={p.clave === actual.clave} onClick={() => (p.clave === "respaldos" ? ir("respaldos") : p.clave === "copias" ? ir("mis-copias") : ir(`configuracion/${p.clave}`))}>{p.nombre}</button>
        ))}
      </nav>
      <div className="cfg-cuerpo">
        {actual.clave === "plan" && (consultorio ? <LicenciaConsultorio /> : <PanelPlan />)}
        {actual.clave === "empresa" && <Empresa />}
        {actual.clave === "sedes" && <Sedes />}
        {actual.clave === "usuarios" && <Usuarios />}
        {actual.clave === "horario" && <><HorarioAtencion /><FeriadosClinica /></>}
        {actual.clave === "tratamientos" && <PanelTratamientos />}
        {actual.clave === "documentos" && <Documentos />}
        {actual.clave === "facturacion" && <PanelFacturacion />}
        {actual.clave === "mensajes" && <PanelConfigMensajes />}
        {actual.clave === "importar" && <ImportarPacientes />}
        {actual.clave === "duplicados" && <DuplicadosPacientes />}
        {actual.clave === "pruebas" && <PacientesPrueba />}
        {actual.clave === "gemini" && <ConfigGemini />}
        {actual.clave === "universidad" && <ConfigUniversidad />}
        {actual.clave === "cuenta" && <MiCuenta />}
      </div>
    </div>
  );
}

/* ---------------- Empresa y marca ---------------- */
function Empresa() {
  const c = useClinica();
  const [f, setF] = useState<Clinica | null>(c);
  const [ok, setOk] = useState("");
  const a = useAccion(), l = useAccion();
  useEffect(() => { if (c && !f) setF(c); }, [c]);
  // vista previa: al tocar un color, toda la app se tiñe al instante; si sales sin guardar, vuelve el color guardado
  const guardado = c?.acento;
  useEffect(() => { if (f && f.acento !== guardado) aplicarAcento(f.acento, false); }, [f?.acento]);
  useEffect(() => () => { const x = document.getElementById("acento-clinica"); if (x && guardadoRef.current) aplicarAcento(guardadoRef.current, false); }, []);
  const guardadoRef = useRef(guardado);
  guardadoRef.current = guardado;
  if (!f) return <Seccion titulo="Empresa y marca"><p className="muted">Cargando…</p></Seccion>;
  const set = (x: Partial<Clinica>) => { setF({ ...f, ...x }); setOk(""); };
  const rucMal = f.ruc && !rucValido(f.ruc);

  async function guardar() {
    const r = await a.correr(() => api<Clinica>("PUT", "/api/clinica", { nombre: f!.nombre, razonSocial: f!.razonSocial, ruc: f!.ruc, correo: f!.correo, telefono: f!.telefono, ciudad: f!.ciudad, institucion: f!.institucion ?? "", unicodigo: f!.unicodigo ?? "", acento: f!.acento, banner: f!.banner }));
    if (r) { await publicarClinica(r); setOk("Datos de la clínica guardados."); }
  }
  async function subirLogo(archivo: File | undefined) {
    if (!archivo) return;
    setOk("");
    const prep = await l.correr(() => prepararLogo(archivo, LOGO_MAX_BYTES));
    if (!prep) return;
    const r = await l.correr(() => api<Clinica>("PUT", "/api/clinica/logo", { imagen: prep.dataUrl }));
    if (r) { await publicarClinica(r); setF({ ...f!, logo: r.logo }); setOk(`Logo actualizado: ya aparece en la cabecera, en el banner y en los documentos impresos.${prep.aviso ? ` ${prep.aviso}` : ""}`); }
  }
  async function quitarLogo() {
    const r = await l.correr(() => api<Clinica>("DELETE", "/api/clinica/logo"));
    if (r) { await publicarClinica(r); setF({ ...f!, logo: null }); setOk("Logo quitado."); }
  }

  return (
    <Seccion titulo="Empresa y marca" acciones={<button className="btn pri" onClick={guardar} disabled={a.ocupado}>Guardar</button>}>
      <p className="muted" style={{ margin: 0 }}>Estos datos identifican a la clínica en la app y en recetas, consentimientos, certificados y recibos.</p>
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      <div className="rejilla">
        <label className="campo">Nombre comercial<input className="entrada" value={f.nombre} maxLength={120} onChange={(e) => set({ nombre: e.target.value })} /></label>
        <label className="campo">Razón social<input className="entrada" value={f.razonSocial} maxLength={160} onChange={(e) => set({ razonSocial: e.target.value })} /></label>
        <label className="campo">RUC<input className="entrada mono" inputMode="numeric" maxLength={13} value={f.ruc} placeholder="13 dígitos" onChange={(e) => set({ ruc: e.target.value.replace(/\D/g, "") })} />
          {f.ruc && <small className={rucMal ? "hint warn" : "hint ok"}>{rucMal ? "Revisa el RUC: 13 dígitos que terminan en 001" : "RUC con formato válido"}</small>}
        </label>
        <label className="campo">Correo de la clínica<input className="entrada" type="email" value={f.correo} onChange={(e) => set({ correo: e.target.value })} /></label>
        <label className="campo">Teléfono<input className="entrada mono" value={f.telefono} onChange={(e) => set({ telefono: e.target.value })} /></label>
        <label className="campo">Ciudad (para certificados)<input className="entrada" value={f.ciudad} onChange={(e) => set({ ciudad: e.target.value })} /></label>
        <label className="campo">Institución del sistema <span className="muted" style={{ fontWeight: 400 }}>(formularios 033 y 024)</span><input className="entrada" value={f.institucion ?? ""} maxLength={120} onChange={(e) => set({ institucion: e.target.value })} /></label>
        <label className="campo">Unicódigo del establecimiento<input className="entrada mono" value={f.unicodigo ?? ""} maxLength={30} onChange={(e) => set({ unicodigo: e.target.value })} /></label>
      </div>
      <AvisoError error={a.error} />

      <h3>Logo</h3>
      <div className="logo-fila">
        <div className="logo-prev">{c?.logoUrl ? <img src={c.logoUrl} alt="Logo actual" /> : <span className="muted">Sin logo</span>}</div>
        <div style={{ display: "grid", gap: 6 }}>
          <div className="fila">
            <label className="btn" style={{ cursor: "pointer" }}>{c?.logoUrl ? "Cambiar logo" : "Subir logo"}
              <input type="file" accept="image/png,image/jpeg,image/webp" hidden aria-label="Archivo del logo" onChange={(e) => { subirLogo(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {c?.logoUrl && <button className="btn chico peligro" onClick={quitarLogo} disabled={l.ocupado}>Quitar</button>}
          </div>
          <small className="muted">PNG, JPG o WebP, con fondo blanco o transparente. Si la imagen pesa más de 500 KB o es de otro tipo (GIF, SVG…), Donna la ajusta sola. Aparece en la pantalla de inicio de sesión, en la cabecera y en todos los documentos impresos.</small>
        </div>
      </div>
      <AvisoError error={l.error} />
      {c?.id && (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Enlace de ingreso con tu marca (para los equipos nuevos de la clínica):{" "}
          <span className="mono" style={{ wordBreak: "break-all" }}>{enlaceIngreso(c.id)}</span>{" "}
          <button type="button" className="btn chico" onClick={() => navigator.clipboard?.writeText(enlaceIngreso(c.id)).then(() => setOk("Enlace copiado."), () => undefined)}>Copiar</button>
        </p>
      )}

      <h3>Color principal</h3>
      <div className="swatches" role="group" aria-label="Color principal">
        {Object.entries(ACENTOS).map(([k, x]) => (
          <button key={k} type="button" className={`swatch${f.acento === k ? " on" : ""}`} aria-pressed={f.acento === k} onClick={() => set({ acento: k })}>
            <span className="mini" aria-hidden="true">{(["--bg", "--surface-2", "--accent", "--ink"] as const).map((v) => <b key={v} style={{ background: paletaDeAcento(k).claro[v] }} />)}</span>{x.nombre}
          </button>
        ))}
      </div>
      <small className="muted">
        {f.acento !== guardado ? "Vista previa: así se verá Donna. Pulsa «Guardar» para aplicarlo a todos los usuarios de la clínica." : "Toda la app usa una paleta de este color: fondo, tarjetas, líneas, textos y botones. El rojo y el azul del odontograma no cambian porque indican «por realizar» y «realizado»."}
      </small>
      <h3>Banner de la clínica</h3>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Aparece en el inicio de sesión y en la página principal: el logotipo a la izquierda, el nombre de la clínica y «With Donna» abajo a la derecha.
        Elige el color de fondo (con un segundo color se forma un degradado). Mira la vista previa y pulsa «Guardar» para aplicarlo.
      </p>
      <label className="firmar"><input type="checkbox" checked={f.banner.activo} onChange={(e) => set({ banner: { ...f.banner, activo: e.target.checked } })} /> Mostrar el banner</label>
      <div className="fila" style={{ alignItems: "end", flexWrap: "wrap" }}>
        <label className="campo">Color de fondo
          <input type="color" className="entrada" style={{ width: 64, padding: 2 }} value={f.banner.color1} onChange={(e) => set({ banner: { ...f.banner, color1: e.target.value.toUpperCase() } })} />
        </label>
        <label className="campo">Segundo color (opcional)
          <input type="color" className="entrada" style={{ width: 64, padding: 2 }} value={f.banner.color2} onChange={(e) => set({ banner: { ...f.banner, color2: e.target.value.toUpperCase() } })} />
        </label>
        <label className="campo">Color del texto
          <select className="entrada" value={f.banner.texto} onChange={(e) => set({ banner: { ...f.banner, texto: e.target.value as typeof f.banner.texto } })}>
            <option value="auto">Automático (según el fondo)</option><option value="claro">Texto claro</option><option value="oscuro">Texto oscuro</option>
          </select>
        </label>
        <button type="button" className="btn chico" onClick={() => set({ banner: bannerPorDefecto(f.acento) })}>Usar el color principal</button>
      </div>
      <div aria-label="Vista previa del banner" style={{ opacity: f.banner.activo ? 1 : 0.45 }}>
        <BannerClinica nombre={f.nombre || "Nombre de la clínica"} logo={c?.logoUrl ?? null} banner={f.banner} />
      </div>
      <small className="muted">{f.banner.activo ? "Vista previa: se aplica a todos los usuarios al pulsar «Guardar»." : "El banner está apagado: no se mostrará."}</small>

    </Seccion>
  );
}

/* ---------------- Sedes ---------------- */
function Sedes() {
  const c = useClinica();
  const [nueva, setNueva] = useState(false);
  if (!c) return <Seccion titulo="Sedes"><p className="muted">Cargando…</p></Seccion>;
  return (
    <Seccion titulo="Sedes" acciones={!nueva && <button className="btn pri" onClick={() => setNueva(true)}>Agregar sede</button>}>
      <p className="muted" style={{ margin: 0 }}>La dirección y el teléfono de la sede principal salen en los documentos impresos.</p>
      <div className="cfg-lista">
        {c.sedes.map((s, i) => <FormSede key={s.id} sede={s} principal={i === c.sedes.findIndex((x) => x.activa)} />)}
        {nueva && <FormSede sede={{ id: "", nombre: "", direccion: "", telefono: "", whatsapp: "", activa: true, es_matriz: false }} alTerminar={() => setNueva(false)} />}
      </div>
    </Seccion>
  );
}

function FormSede({ sede, principal, alTerminar }: { sede: Sede; principal?: boolean; alTerminar?: () => void }) {
  const [s, setS] = useState(sede);
  const [ok, setOk] = useState("");
  const a = useAccion();
  const set = (x: Partial<Sede>) => { setS({ ...s, ...x }); setOk(""); };
  async function guardar(e: FormEvent) {
    e.preventDefault();
    const r = await a.correr(() => api<Clinica>(s.id ? "PUT" : "POST", s.id ? `/api/sedes/${s.id}` : "/api/sedes", s));
    if (r) { await publicarClinica(r); setOk("Guardada."); alTerminar?.(); }
  }
  const [borrando, setBorrando] = useState(false);
  async function borrar() {
    const r = await a.correr(() => api<Clinica>("DELETE", `/api/sedes/${s.id}`));
    if (r) await publicarClinica(r);
  }
  return (
    <form className={`cfg-card${s.activa ? "" : " off"}`} onSubmit={guardar} aria-label={s.id ? `Sede ${sede.nombre}` : "Nueva sede"}>
      <div className="fila" style={{ justifyContent: "space-between" }}>
        <b>{s.id ? sede.nombre : "Nueva sede"}{sede.es_matriz && <span className="chip e-confirmada" style={{ marginLeft: 8 }}>Matriz</span>}{principal && !sede.es_matriz && <span className="chip e-confirmada" style={{ marginLeft: 8 }}>Principal</span>}</b>
        {s.id && <label className="firmar"><input type="checkbox" checked={s.activa} onChange={(e) => set({ activa: e.target.checked })} /> Activa</label>}
      </div>
      <div className="rejilla">
        <label className="campo">Nombre de la sede<input className="entrada" required maxLength={80} value={s.nombre} onChange={(e) => set({ nombre: e.target.value })} /></label>
        <label className="campo">Dirección<input className="entrada" maxLength={200} value={s.direccion} placeholder="Av. Amazonas N34-120 y Colón, Quito" onChange={(e) => set({ direccion: e.target.value })} /></label>
        <label className="campo">Teléfono<input className="entrada mono" value={s.telefono} onChange={(e) => set({ telefono: e.target.value })} /></label>
        <label className="campo">WhatsApp de la sede<input className="entrada mono" inputMode="tel" placeholder="09XXXXXXXX" value={s.whatsapp} onChange={(e) => set({ whatsapp: e.target.value })} /></label>
      </div>
      {s.id && s.activa && <label className="firmar"><input type="checkbox" checked={s.es_matriz} disabled={sede.es_matriz} onChange={(e) => set({ es_matriz: e.target.checked })} /> Sede matriz <span className="muted">(la administración y el personal de la matriz ven la agenda de todas las sedes; el de otra sede, solo la suya)</span></label>}
      <AvisoError error={a.error} />
      <div className="fila">
        <button className="btn pri chico" disabled={a.ocupado}>{s.id ? "Guardar sede" : "Crear sede"}</button>
        {!s.id && <button type="button" className="btn chico" onClick={alTerminar}>Cancelar</button>}
        {s.id && !sede.es_matriz && !borrando && <button type="button" className="btn chico peligro" onClick={() => setBorrando(true)}>Borrar sede</button>}
        {borrando && (
          <span className="fila" style={{ gap: 6 }}>
            <span style={{ fontSize: 13 }}>¿Borrar «{sede.nombre}»? Solo se puede si no tiene citas de hoy en adelante ni personal asignado; las citas anteriores quedan sin sede.</span>
            <button type="button" className="btn chico peligro" disabled={a.ocupado} onClick={borrar}>Sí, borrar</button>
            <button type="button" className="btn chico" onClick={() => setBorrando(false)}>No</button>
          </span>
        )}
        {ok && <span className="muted" style={{ fontSize: 13 }}>{ok}</span>}
      </div>
    </form>
  );
}

/* ---------------- Usuarios y roles ---------------- */
interface UsuarioApi { id: string; nombre: string; email: string; rol: RolUsuario; registro_profesional: string; telefono: string; color: string | null; activo: boolean; debe_cambiar: boolean; ultimo_acceso: string | null; creado: string; sede_id: string | null; vinculada: boolean; en_varias: boolean }
interface Rol { clave: RolUsuario; nombre: string; puede: string; noPuede: string }
type Borrador = { id: string; nombre: string; email: string; rol: RolUsuario; registro: string; telefono: string; activo: boolean; color: string; sedeId: string };

const vacio = (): Borrador => ({ id: "", nombre: "", email: "", rol: "odontologo", registro: "", telefono: "", activo: true, color: "", sedeId: "" });
const fechaCorta = (iso: string | null) => (iso ? new Date(iso).toLocaleString("es-EC", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil" }) : "Nunca");

function Usuarios() {
  const [datos, setDatos] = useState<{ usuarios: UsuarioApi[]; roles: Rol[] } | null>(null);
  const [editando, setEditando] = useState<Borrador | null>(null);
  const [clave, setClave] = useState<{ nombre: string; email: string; clave: string } | null>(null);
  const a = useAccion(), g = useAccion();
  const yo = leerSesion()?.usuario.id;
  const sedesActivas = (useClinica()?.sedes ?? []).filter((x) => x.activa);
  const cargar = () => a.correr(() => api<{ usuarios: UsuarioApi[]; roles: Rol[] }>("GET", "/api/usuarios")).then((r) => r && setDatos(r));
  useEffect(() => { cargar(); }, []);

  async function guardar(e: FormEvent) {
    e.preventDefault();
    const b = editando!;
    if (!b.id) {
      const r = await g.correr(() => api<{ usuario: UsuarioApi; claveTemporal: string }>("POST", "/api/usuarios", b));
      if (r) { setClave({ nombre: r.usuario.nombre, email: r.usuario.email, clave: r.claveTemporal }); setEditando(null); cargar(); }
    } else {
      const r = await g.correr(() => api<UsuarioApi>("PUT", `/api/usuarios/${b.id}`, b));
      if (r) { setEditando(null); cargar(); }
    }
  }
  async function restablecer(u: UsuarioApi) {
    if (!confirm(`¿Crear una contraseña temporal para ${u.nombre}? Su contraseña actual dejará de funcionar y se cerrarán sus sesiones abiertas.`)) return;
    const r = await g.correr(() => api<{ claveTemporal: string }>("POST", `/api/usuarios/${u.id}/clave`));
    if (r) { setClave({ nombre: u.nombre, email: u.email, clave: r.claveTemporal }); cargar(); }
  }

  if (!datos) return <Seccion titulo="Usuarios y roles"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;
  const rol = (k: string) => datos.roles.find((r) => r.clave === k);
  return (
    <>
      <Seccion titulo="Usuarios y roles" acciones={!editando && <button className="btn pri" onClick={() => { setEditando(vacio()); setClave(null); }}>Agregar usuario</button>}>
        {clave && (
          <div className="aviso ok clave-temporal" role="status">
            <b>Contraseña temporal de {clave.nombre}</b>
            <div>Correo: <span className="mono">{clave.email}</span> · Contraseña: <span className="mono clave">{clave.clave}</span></div>
            <small>Entrégasela en persona o por un canal seguro. Solo se muestra esta vez; al entrar, Donna le pedirá crear su propia contraseña.</small>
            <div><button className="btn chico" onClick={() => { navigator.clipboard?.writeText(`${clave.email} · ${clave.clave}`).catch(() => undefined); }}>Copiar</button> <button className="btn chico" onClick={() => setClave(null)}>Listo</button></div>
          </div>
        )}
        {editando && (
          <form className="cfg-card" onSubmit={guardar} aria-label={editando.id ? "Editar usuario" : "Nuevo usuario"}>
            <b>{editando.id ? `Editar: ${editando.nombre}` : "Nuevo usuario"}</b>
            <div className="rejilla">
              <label className="campo">Nombre con título<input className="entrada" required value={editando.nombre} placeholder="Dra. Ana Pérez" onChange={(e) => setEditando({ ...editando, nombre: e.target.value })} /></label>
              <label className="campo">Correo (para entrar)<input className="entrada" type="email" required value={editando.email} onChange={(e) => setEditando({ ...editando, email: e.target.value })} /></label>
              <label className="campo">Rol<select className="entrada" disabled={editando.rol === "laboratorio"} value={editando.rol} onChange={(e) => setEditando({ ...editando, rol: e.target.value as RolUsuario })}>
                {editando.rol === "laboratorio" && <option value="laboratorio">Laboratorio dental</option>}
                {datos.roles.map((r) => <option key={r.clave} value={r.clave}>{r.nombre}</option>)}
              </select></label>
              <label className="campo">Registro profesional<input className="entrada" value={editando.registro} placeholder="Registro SENESCYT / ACESS" onChange={(e) => setEditando({ ...editando, registro: e.target.value })} /></label>
              <label className="campo">Celular<input className="entrada mono" inputMode="tel" value={editando.telefono} placeholder="09XXXXXXXX" onChange={(e) => setEditando({ ...editando, telefono: e.target.value })} /></label>
              {sedesActivas.length > 1 && ["recepcion", "asistente"].includes(editando.rol) && (
                <label className="campo">Sede donde trabaja
                  <select className="entrada" value={editando.sedeId} onChange={(e) => setEditando({ ...editando, sedeId: e.target.value })}>
                    <option value="">Sin asignar (ve todas las sedes)</option>
                    {sedesActivas.map((x) => <option key={x.id} value={x.id}>{x.nombre}{x.es_matriz ? " (matriz)" : ""}</option>)}
                  </select>
                  <small className="hint">El personal de la sede matriz ve la agenda de todas las sedes; el de otra sede, solo la de su sede.</small>
                </label>
              )}
              {editando.id && <label className="campo">Color en la agenda<input type="color" className="entrada" style={{ padding: 2, height: 38 }} value={editando.color || "#0A6E5C"} onChange={(e) => setEditando({ ...editando, color: e.target.value })} /></label>}
            </div>
            <div className="aviso info" style={{ fontSize: 13 }}><b>{rol(editando.rol)?.nombre}:</b> {rol(editando.rol)?.puede}{rol(editando.rol)?.noPuede !== "—" && <> <b>No puede:</b> {rol(editando.rol)?.noPuede}</>}</div>
            {editando.id && editando.id !== yo && <label className="firmar"><input type="checkbox" checked={editando.activo} onChange={(e) => setEditando({ ...editando, activo: e.target.checked })} /> Usuario activo (desmárcalo si ya no trabaja en la clínica; su historial se conserva)</label>}
            {editando.id && <small className="muted">Si cambias el rol o lo desactivas, sus sesiones abiertas se cierran al instante.</small>}
            <AvisoError error={g.error} />
            <div className="fila">
              <button className="btn pri" disabled={g.ocupado}>{editando.id ? "Guardar cambios" : "Crear usuario"}</button>
              <button type="button" className="btn" onClick={() => setEditando(null)}>Cancelar</button>
            </div>
          </form>
        )}
        <div className="tabla-scroll">
          <table className="tabla-resp" aria-label="Usuarios">
            <thead><tr><th>Nombre</th><th>Rol</th><th>Correo</th><th>Último acceso</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {datos.usuarios.map((u) => (
                <tr key={u.id} className={u.activo ? "" : "inactivo"}>
                  <td>{u.color && <><span className="punto" style={{ background: u.color }} /> </>}<b>{u.nombre}</b>{u.id === yo && <span className="muted"> (tú)</span>}{u.registro_profesional && <div className="muted" style={{ fontSize: 12 }}>{u.registro_profesional}</div>}</td>
                  <td>{rol(u.rol)?.nombre ?? u.rol}{u.sede_id && <div className="muted" style={{ fontSize: 12 }}>{sedesActivas.find((x) => x.id === u.sede_id)?.nombre ?? ""}</div>}</td>
                  <td className="mono" style={{ fontSize: 13 }}>{u.email}</td>
                  <td style={{ fontSize: 13 }}>{fechaCorta(u.ultimo_acceso)}</td>
                  <td>{u.en_varias && <span className="chip" title="Trabaja también en otra clínica" style={{ marginRight: 4 }}>{u.vinculada ? "Cuenta vinculada" : "En varias clínicas"}</span>}{!u.activo ? <span className="chip">Inactivo</span> : u.debe_cambiar ? <span className="chip rojo">Clave temporal</span> : <span className="chip e-confirmada">Activo</span>}</td>
                  <td className="acciones-fila">
                    <button className="btn chico" onClick={() => { setClave(null); setEditando({ id: u.id, nombre: u.nombre, email: u.email, rol: u.rol, registro: u.registro_profesional, telefono: u.telefono, sedeId: u.sede_id ?? "", activo: u.activo, color: u.color ?? "" }); }}>Editar</button>
                    {u.id !== yo && u.activo && !u.vinculada && <button className="btn chico" onClick={() => restablecer(u)}>Nueva contraseña</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AvisoError error={a.error} />
      </Seccion>
      <Seccion titulo="Profesionales de otras clínicas"><InvitarProfesional /></Seccion>
      <Seccion titulo="Qué puede hacer cada rol">
        <div className="tabla-scroll">
          <table className="tabla-resp" aria-label="Permisos por rol">
            <thead><tr><th>Rol</th><th>Puede</th><th>No puede</th></tr></thead>
            <tbody>{datos.roles.map((r) => <tr key={r.clave}><td><b>{r.nombre}</b></td><td>{r.puede}</td><td>{r.noPuede}</td></tr>)}</tbody>
          </table>
        </div>
        <small className="muted">Los permisos se revisan en el servidor en cada acción, no solo en la pantalla.</small>
      </Seccion>
    </>
  );
}

/* ---------------- Horario de atención ---------------- */
const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
type Horario = Record<string, [string, string] | null>;

function HorarioAtencion() {
  const clinica = useClinica();
  const sedes = (clinica?.sedes ?? []).filter((x) => x.activa);
  const [sedeId, setSedeId] = useState("");
  const [h, setH] = useState<Horario | null>(null);
  const [ok, setOk] = useState("");
  const a = useAccion();
  const sede = sedes.find((x) => x.id === sedeId) ?? sedes[0];
  // cada sede tiene su propio horario: al cambiar de sede se carga el de esa sede
  useEffect(() => {
    if (!sede) return;
    setH(null); setOk("");
    api<Horario>("GET", `/api/agenda/horario?sede=${sede.id}`).then(setH).catch(a.setError);
  }, [sede?.id]);
  if (!sede || !h) return <Seccion titulo="Horario de atención"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;
  const set = (d: number, v: [string, string] | null) => { setH({ ...h, [d]: v }); setOk(""); };
  async function guardar() {
    const r = await a.correr(() => api<Horario>("PUT", "/api/agenda/horario", { sedeId: sede!.id, horario: h }));
    if (r) { setH(r); setOk(`Horario de ${sede!.nombre} guardado. La agenda avisa si una cita cae fuera de él.`); }
  }
  async function copiarDe(id: string) {
    const r = await a.correr(() => api<Horario>("GET", `/api/agenda/horario?sede=${id}`));
    if (r) { setH(r); setOk("Horario copiado: revísalo y pulsa «Guardar»."); }
  }
  return (
    <Seccion titulo={sedes.length > 1 ? `Horario de atención · ${sede.nombre}` : "Horario de atención"} acciones={<button className="btn pri" onClick={guardar} disabled={a.ocupado}>Guardar</button>}>
      {sedes.length > 1 && (
        <div className="fila" style={{ alignItems: "end", flexWrap: "wrap" }}>
          <label className="campo" style={{ flex: "0 1 260px" }}>Sede
            <select className="entrada" value={sede.id} onChange={(e) => setSedeId(e.target.value)} aria-label="Sede del horario">{sedes.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select></label>
          <label className="campo" style={{ flex: "0 1 260px" }}>Copiar el horario de otra sede
            <select className="entrada" value="" onChange={(e) => { if (e.target.value) copiarDe(e.target.value); }} aria-label="Copiar el horario de">
              <option value="">Elegir sede…</option>{sedes.filter((x) => x.id !== sede.id).map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select></label>
        </div>
      )}
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Cada sede tiene su propio horario: la agenda avisa cuando una cita de esa sede cae fuera de él.</p>
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      <div className="horario">
        {[1, 2, 3, 4, 5, 6, 0].map((d) => {
          const v = h[d];
          return (
            <div key={d} className="horario-dia">
              <label className="firmar"><input type="checkbox" checked={!!v} onChange={(e) => set(d, e.target.checked ? ["08:00", "18:00"] : null)} /> <b>{DIAS[d]}</b></label>
              {v ? (
                <span className="fila" style={{ gap: 6 }}>
                  <input className="entrada" type="time" aria-label={`Apertura del ${DIAS[d]}`} value={v[0]} onChange={(e) => set(d, [e.target.value, v[1]])} />
                  <span className="muted">a</span>
                  <input className="entrada" type="time" aria-label={`Cierre del ${DIAS[d]}`} value={v[1]} onChange={(e) => set(d, [v[0], e.target.value])} />
                </span>
              ) : <span className="muted">Cerrado</span>}
            </div>
          );
        })}
      </div>
      <AvisoError error={a.error} />
    </Seccion>
  );
}

/* ---------------- Documentos y cobros ---------------- */
interface Docs { ivaPct: number; ciudad: string; recetaTamano: "a4" | "a5"; numeracion: { clave: string; nombre: string; usado: number; proximo: number }[] }

function Documentos() {
  const [d, setD] = useState<Docs | null>(null);
  const [prox, setProx] = useState<Record<string, string>>({});
  const [ok, setOk] = useState("");
  const a = useAccion();
  const cargar = (x: Docs) => { setD(x); setProx(Object.fromEntries(x.numeracion.map((n) => [n.clave, String(n.proximo)]))); };
  useEffect(() => { api<Docs>("GET", "/api/config/documentos").then(cargar).catch(a.setError); }, []);
  if (!d) return <Seccion titulo="Documentos y cobros"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;
  async function guardar() {
    const numeracion = Object.fromEntries(Object.entries(prox).map(([k, v]) => [k, Number(v)]));
    const r = await a.correr(() => api<Docs>("PUT", "/api/config/documentos", { ivaPct: d!.ivaPct, ciudad: d!.ciudad, recetaTamano: d!.recetaTamano, numeracion }));
    if (r) { cargar(r); setOk("Guardado."); }
  }
  return (
    <Seccion titulo="Documentos y cobros" acciones={<button className="btn pri" onClick={guardar} disabled={a.ocupado}>Guardar</button>}>
      {ok && <div className="aviso ok" role="status">{ok}</div>}
      <div className="rejilla">
        <label className="campo">IVA en presupuestos
          <select className="entrada" value={d.ivaPct} onChange={(e) => { setD({ ...d, ivaPct: Number(e.target.value) }); setOk(""); }}>
            <option value={0}>0 % (servicios de salud)</option><option value={5}>5 %</option><option value={15}>15 %</option>
          </select>
          <small className="hint">Los servicios de salud tienen tarifa 0 %. Confírmalo con tu contador para tratamientos estéticos.</small>
        </label>
        <label className="campo">Ciudad en los documentos<input className="entrada" value={d.ciudad} placeholder="Quito" onChange={(e) => { setD({ ...d, ciudad: e.target.value }); setOk(""); }} /></label>
        <label className="campo">Tamaño de la receta
          <select className="entrada" value={d.recetaTamano} onChange={(e) => { setD({ ...d, recetaTamano: e.target.value === "a5" ? "a5" : "a4" }); setOk(""); }}>
            <option value="a4">A4 (hoja completa)</option><option value="a5">A5 (media hoja)</option>
          </select>
          <small className="hint">Los demás documentos siempre salen en A4 vertical. Las recetas ya firmadas no cambian de tamaño.</small>
        </label>
      </div>
      <h3>Próximos números</h3>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Útil si vienes de otro sistema: continúa la numeración que ya tenías. Solo se puede subir, nunca repetir un número ya usado.</p>
      <div className="rejilla">
        {d.numeracion.map((n) => (
          <label key={n.clave} className="campo">{n.nombre}
            <input className="entrada mono" inputMode="numeric" value={prox[n.clave] ?? ""} onChange={(e) => { setProx({ ...prox, [n.clave]: e.target.value.replace(/\D/g, "") }); setOk(""); }} />
            <small className="hint">{n.usado ? `Último usado: ${n.usado}` : "Todavía no se ha usado"}</small>
          </label>
        ))}
      </div>
      <AvisoError error={a.error} />
    </Seccion>
  );
}

/* ---------------- Mi cuenta y cambio de contraseña ---------------- */
function MiCuenta() {
  const s = leerSesion();
  const [hecho, setHecho] = useState(false);
  return (
    <Seccion titulo="Mi cuenta">
      <p style={{ margin: 0 }}><b>{s?.usuario.nombre}</b>{s?.usuario.email && <span className="muted"> · {s.usuario.email}</span>}<br />
        <span className="muted">Rol: {DESCRIPCION_ROL[s?.usuario.rol as RolUsuario]?.nombre ?? s?.usuario.rol}{s?.usuario.registro && ` · ${s.usuario.registro}`}</span></p>
      {hecho && <div className="aviso ok" role="status">Contraseña cambiada. Se cerraron tus sesiones en otros dispositivos.</div>}
      <FormClave key={String(hecho)} alCambiar={(token) => { const x = leerSesion(); if (x) guardarSesion({ ...x, token }); setHecho(true); }} />
      <h3 style={{ margin: "12px 0 0" }}>Verificación en dos pasos</h3>
      <DosPasos />
      <h3 style={{ margin: "12px 0 0" }}>Voz de Donna</h3>
      <VozDeDonna />
    </Seccion>
  );
}

export function FormClave({ alCambiar }: { alCambiar: (token: string) => void }) {
  const [actual, setActual] = useState(""), [nueva, setNueva] = useState(""), [rep, setRep] = useState("");
  const a = useAccion();
  const problema = nueva && nueva.length < 10 ? "Al menos 10 caracteres." : nueva && (!/[a-zA-Z]/.test(nueva) || !/\d/.test(nueva)) ? "Combina letras y números." : rep && rep !== nueva ? "Las contraseñas no coinciden." : "";
  async function enviar(e: FormEvent) {
    e.preventDefault();
    const r = await a.correr(() => api<{ token: string }>("POST", "/api/auth/clave", { actual, nueva }));
    if (r) alCambiar(r.token);
  }
  return (
    <form onSubmit={enviar} className="form-clave">
      <CampoClave etiqueta="Contraseña actual" autoComplete="current-password" required value={actual} onChange={setActual} />
      <CampoClave etiqueta="Contraseña nueva" autoComplete="new-password" required value={nueva} onChange={setNueva} />
      <CampoClave etiqueta="Repite la contraseña nueva" autoComplete="new-password" required value={rep} onChange={setRep} />
      {problema ? <small className="hint warn">{problema}</small> : <small className="hint">Al menos 10 caracteres, con letras y números.</small>}
      <AvisoError error={a.error} />
      <div><button className="btn pri" disabled={a.ocupado || !!problema || !nueva || rep !== nueva}>Cambiar contraseña</button></div>
    </form>
  );
}

/** Pantalla que aparece al entrar con una contraseña temporal. */
export function CambiarClave({ obligatoria, alCambiar, alSalir }: { obligatoria?: boolean; alCambiar: (token: string) => void; alSalir: () => void }) {
  const s = leerSesion();
  return (
    <main className="login">
      <div className="card" style={{ display: "grid", gap: 12 }}>
        <h1 style={{ fontSize: 22, margin: 0 }}>Crea tu contraseña</h1>
        <p className="muted" style={{ margin: 0 }}>{obligatoria ? `Hola, ${s?.usuario.nombre ?? ""}. Entraste con una contraseña temporal: crea una propia para continuar.` : ""}</p>
        <FormClave alCambiar={alCambiar} />
        <button className="btn" onClick={alSalir}>Salir</button>
      </div>
    </main>
  );
}
